import Vue from "vue";
import Router from "vue-router";
import { store } from "./store";
import ROLE_TYPES from "./roles/roles";
import buildingsRules from "@/roles/rules/buildings";

Vue.use(Router);

// Lazy load module
function lazyLoad(path) {
  return () => import(`@/components/${path}`);
}

const router = new Router({
  routes: [
    {
      path: "/login",
      name: "login",
      component: lazyLoad("user/LoginComponent"),
    },
    {
      path: "/app",
      name: "main",
      component: lazyLoad("HomeViewComponent"),
      beforeEnter(to, from, next) {
        const role = store.getters["authStore/getUserRole"];
        if (role && role === ROLE_TYPES.TENANT) {
          next("/tenant");
        } else {
          next();
        }
      },
      children: [
        {
          path: "dashboard",
          name: "dashboard",
          component: lazyLoad("dashboard/DashboardComponent"),
        },
        {
          path: "/enforcements",
          name: "enforcements",
          component: lazyLoad("enforcements/EnforcementsComponent"),
          redirect: { name: "enforcements.recentEnforcement" },
          meta: {
            breadcrumb: "Tow Enforcements",
          },
          children: [
            {
              path: "dashboard",
              name: "enforcements.Dashboard",
              component: lazyLoad("enforcements/DashboardComponent"),
              meta: {
                breadcrumb: "Dashboard",
              },
            },
            {
              path: "recent",
              name: "enforcements.recentEnforcement",
              component: lazyLoad("enforcements/RecentComponent"),
              meta: {
                breadcrumb: "Recent Enforcement",
              },
              beforeEnter: (to, from, next) => {
                const role = store.getters["authStore/getUserRole"];
                const blockedRoles = [ROLE_TYPES.TOW_DRIVER];
                if (role && blockedRoles.includes(role)) {
                  next({ name: "enforcements.Dashboard" });
                } else {
                  next();
                }
              },
            },
            {
              path: "pending-enforcement",
              name: "enforcements.pendingEnforcement",
              component: lazyLoad("enforcements/PendingEnforcementComponent"),
              meta: {
                breadcrumb: "Pending Enforcement",
              },
            },
            // {
            //   path: 'do-not-tow',
            //   name: 'enforcements.doNotTowEnforcement',
            //   component: lazyLoad('enforcements/DoNotTowComponent'),
            //   meta: {
            //     breadcrumb: 'Do Not Tow'
            //   }
            // },
            {
              path: "pending-decisions",
              name: "enforcements.pendingDecisions",
              component: lazyLoad("enforcements/PendingDecisionsComponent"),
              meta: {
                breadcrumb: "Pending Decisions",
              },
            },
            {
              path: "closed",
              name: "enforcements.closed",
              component: lazyLoad("enforcements/ClosedVehicleComponent"),
              meta: {
                breadcrumb: "Closed",
              },
            },
          ],
        },
        {
          path: "/buildings",
          name: "buildings",
          component: lazyLoad("buildings/BuildingsComponent"),
          meta: {
            breadcrumb: "Properties",
          },
          children: [
            {
              path: "add",
              name: "buildings.add.complex",
              component: lazyLoad("buildings/complex/AddComplexComponent"),
              meta: {
                breadcrumb: "Add New Complex",
              },
            },
            {
              path: ":id",
              name: "buildings.complex",
              component: lazyLoad("buildings/ComplexComponent"),
              redirect: { name: "buildings.complex.towingProcess" },
              children: [
                {
                  path: "guest-permits",
                  name: "buildings.complex.guestPermits",
                  component: lazyLoad(
                    "buildings/v2permits/guest/GuestPermitComponent"
                  ),
                  meta: {
                    breadcrumb: "Guest Permits",
                  },
                },
                {
                  path: "resident-permits",
                  name: "buildings.complex.residentPermits",
                  component: lazyLoad(
                    "buildings/v2permits/resident/ResidentPermitComponent"
                  ),
                  meta: {
                    breadcrumb: "Resident Permits",
                  },
                },
                {
                  path: "reserved-permits",
                  name: "buildings.complex.reservedPermits",
                  component: lazyLoad(
                    "buildings/v2permits/reserved/ReservedPermitComponent"
                  ),
                  meta: {
                    breadcrumb: "Reserved Permits",
                  },
                },
                {
                  path: "do-not-tow",
                  name: "buildings.complex.doNotTow",
                  component: lazyLoad(
                    "buildings/v2permits/doNotTow/DoNotTowComponent"
                  ),
                  meta: {
                    breadcrumb: "Do Not Tow",
                  },
                },
                {
                  path: "blocked-vehicles",
                  name: "buildings.complex.blockedVehicles",
                  component: lazyLoad(
                    "buildings/v2permits/blocked/BlockedVehiclesComponent"
                  ),
                  meta: {
                    breadcrumb: "Blocked Vehicles",
                  },
                },
                {
                  path: "tenants",
                  name: "buildings.complex.tenantRegistration",
                  component: lazyLoad(
                    "buildings/tenant-registration/TenantRegistration"
                  ),
                  meta: {
                    breadcrumb: "Tenants",
                  },
                  /* beforeEnter: (to, from, next) => {
                    const role = store.getters["authStore/getUserRole"];
                    const allowedRoles =
                      buildingsRules.CAN_ENTER_TENANT_REGISTRATION_ROUTE;
                    if (role && allowedRoles.includes(role)) {
                      next();
                    } else {
                      next("/");
                    }
                  }, */
                },
                {
                  path: "apartment-details",
                  name: "buildings.complex.appartmentDetails",
                  component: lazyLoad(
                    "buildings/v2apartment-details/ApartmentDetailsComponent"
                  ),
                  meta: {
                    breadcrumb: "Apartment Details",
                  },
                },
                {
                  path: "payments",
                  name: "buildings.complex.payments",
                  component: lazyLoad("buildings/v2payments/PaymentsComponent"),
                  meta: {
                    breadcrumb: "Payments",
                  },
                  beforeEnter: (to, from, next) => {
                    const role = store.getters["authStore/getUserRole"];
                    const blockedRoles = [
                      ROLE_TYPES.TOW_DRIVER,
                      ROLE_TYPES.LEASING_STAFF,
                    ];
                    if (role && blockedRoles.includes(role)) {
                      next("/buildings");
                    } else {
                      next();
                    }
                  },
                },
                {
                  path: "accounts",
                  name: "accounts",
                  component: lazyLoad("buildings/v2accounts/AccountsComponent"),
                  meta: {
                    breadcrumb: "Accounts",
                  },
                  beforeEnter: (to, from, next) => {
                    const role = store.getters["authStore/getUserRole"];
                    const blockedRoles =
                      buildingsRules.CAN_NOT_ACCESS_ACCOUNT_PERMIT_MENU;
                    if (role && blockedRoles.includes(role)) {
                      next("/dashboard");
                    } else {
                      next();
                    }
                  },
                },
                {
                  path: "patrolLog",
                  name: "buildings.complex.patrolLog",
                  component: lazyLoad("buildings/patrolLog/PatrolLog"),
                  meta: {
                    breadcrumb: "Patrol Logs",
                  },
                },
                {
                  path: "towingProcess",
                  name: "buildings.complex.towingProcess",
                  component: lazyLoad(
                    "buildings/towing/TowingProcessComponent"
                  ),
                  meta: {
                    breadcrumb: "Towing Process",
                  },
                },
                {
                  path: "search/:searchString",
                  name: "buildings.complex.search",
                  component: lazyLoad("buildings/search/Search"),
                  props: true,
                  meta: {
                    breadcrumb: "Search",
                  },
                },
              ],
            },
          ],
        },
        {
          path: "/mainAccounts",
          name: "mainAccounts",
          component: lazyLoad("v2mainaccounts/MainAccountsComponent"),
          meta: {
            breadcrumb: "Main Accounts",
          },
        },
        {
          path: "/mainreports",
          name: "mainReports",
          component: lazyLoad("v2mainreports/MainReportsComponent"),
          meta: {
            breadcrumb: "Reports",
          },
          beforeEnter: (to, from, next) => {
            const role = store.getters["authStore/getUserRole"];
            const blockedRoles = [
              ROLE_TYPES.TOW_DRIVER,
              ROLE_TYPES.PB_FIELD_AGENT,
            ];

            if (role && blockedRoles.includes(role)) {
              next("/dashboard");
            } else {
              next();
            }
          },
        },
        {
          path: "/logs",
          name: "logs",
          component: lazyLoad("v2logs/LogComponent"),
          meta: {
            breadcrumb: "Logs",
          },
          beforeEnter: (to, from, next) => {
            const role = store.getters["authStore/getUserRole"];
            const allowedRoles = [
              ROLE_TYPES.ADMIN,
              ROLE_TYPES.MANAGER,
              ROLE_TYPES.TOW_COMPANY,
            ];
            if (!allowedRoles.includes(role)) {
              next("/dashboard");
            } else {
              next();
            }
          },
        },
        {
          path: "/patrol",
          name: "patrol",
          component: lazyLoad("patrol/PatrolComponent"),
          meta: {
            breadcrumb: "Patrol",
          },
          beforeEnter: (to, from, next) => {
            const role = store.getters["authStore/getUserRole"];
            const allowedRoles = [ROLE_TYPES.ADMIN, ROLE_TYPES.TOW_COMPANY];
            if (!allowedRoles.includes(role)) {
              next("/dashboard");
            } else {
              next();
            }
          },
        },
        {
          path: "/profile",
          name: "profile",
          component: lazyLoad("user/ProfileComponent"),
        },
        {
          path: "/notifications",
          name: "notifications",
          component: lazyLoad("notifications/NotificationsComponent"),
        },
      ],
    },
    {
      path: "/guest",
      name: "guest",
      component: lazyLoad("user/GuestComponent"),
    },
    {
      path: "/resident",
      name: "resident",
      component: lazyLoad("user/ResidentsRegistrationComponent"),
    },
    {
      path: "/new-tenant-profile",
      name: "tenants",
      component: lazyLoad("user/tenant/TenantsRegistration"),
    },
    {
      path: "/parking-lot",
      name: "parkingLot",
      component: lazyLoad("user/GuestComponent"),
    },
    {
      path: "/guest-status/:gid",
      name: "guest.status",
      component: lazyLoad("user/GuestRegistrationStatus"),
      props: true,
    },
    {
      path: "/resident-status/:gid",
      name: "resident.status",
      component: lazyLoad("user/ResidentRegistrationStatus"),
      props: true,
    },
    {
      path: "/reset-password/:token",
      name: "reset.password",
      component: lazyLoad("user/ResetPassword"),
    },
    {
      path: "/tenant",
      name: "tenant.dashboard",
      component: lazyLoad("tenant-dashboard/TenantDashboard"),
      beforeEnter(to, from, next) {
        const role = store.getters["authStore/getUserRole"];
        if (role && role === ROLE_TYPES.TENANT) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "*",
      redirect: "/login",
    },
  ],
});

router.beforeEach((to, from, next) => {
  to.params.previous = from;
  if (!to.meta.breadcrumb) {
    to.meta.breadcrumb = to.params.name;
  }

  let accessToken = null;
  const storage = localStorage.getItem("vuex");
  if (storage) {
    accessToken = JSON.parse(storage).authStore.token;
  }

  if (
    accessToken &&
    (to.path === "/login" ||
      to.path === "/guest" ||
      to.path === "/resident" ||
      to.path === "/parking-lot")
  ) {
    next("/app/dashboard");
  } else if (!accessToken) {
    if (
      to.name === "login" ||
      to.name === "guest" ||
      to.name === "guest.status" ||
      to.name === "resident.status" ||
      to.name === "reset.password" ||
      to.path === "/parking-lot" ||
      to.name === "resident" ||
      to.name === "tenants"
    ) {
      next();
    } else {
      next("/login");
    }
  } else {
    next();
  }
});

export default router;



// WEBPACK FOOTER //
// ./src/router.js